import { useEffect, useState } from 'react';
import { backupAddresses, type AddressItem } from '@/mocks/home';
import StatusPill from './StatusPill';

const toneMap: Record<AddressItem['tone'], 'ok' | 'warn' | 'busy'> = {
  ok: 'ok',
  warn: 'warn',
  busy: 'busy',
};

export default function BackupAddresses() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!copiedId) return undefined;
    const timer = window.setTimeout(() => setCopiedId(null), 1600);
    return () => window.clearTimeout(timer);
  }, [copiedId]);

  return (
    <section id="addresses" className="scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-medium tracking-[0.2em] text-primary-600">备用地址</span>
            <h2 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              常用入口一栏看全
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
              主线路打不开时，优先选用状态为「正常可用」的线路。所有线路内容与主站一致，地址变更后本页会同步更新。
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-secondary-200 bg-secondary-100 px-4 py-2 text-xs font-medium text-secondary-900">
            <i className="ri-radar-line text-sm"></i>
            地址每 10 分钟同步一次
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          {backupAddresses.map((item) => (
            <div
              key={item.id}
              className={`flex flex-col rounded-lg border p-5 transition-colors duration-200 ${
                item.primary
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-background-200 bg-background-50 hover:border-background-300'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{item.name}</h3>
                  <span className="mt-1 inline-block rounded bg-background-200/70 px-2 py-0.5 font-label text-[11px] tracking-widest text-foreground-600">
                    {item.code}
                  </span>
                </div>
                <StatusPill label={item.status} tone={toneMap[item.tone]} />
              </div>

              <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground-600">{item.note}</p>

              <div className="mt-5 flex items-center justify-between gap-3 border-t border-background-200/80 pt-4">
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-history-line"></i>
                  {item.updated}
                </span>
                <button
                  type="button"
                  onClick={() => setCopiedId(item.id)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${
                    copiedId === item.id
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i className={copiedId === item.id ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copiedId === item.id ? '已复制' : '复制地址'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}